// saas-monetization-plans v2 — WU-M: CancelSubscriptionDialog.
//
// Legal-friction cancellation flow (design §6, spec "Subscription Lifecycle
// — Legal cancellation"). The backend NEVER obstructs an actual
// cancellation — this dialog is UI-only, skippable friction:
//   confirm -> retention offer (skippable) -> exit survey (skippable) -> cancel
// Accepting the retention offer simply closes the dialog WITHOUT calling the
// cancel endpoint at all — declining or skipping any step always reaches a
// real cancel call. `reason`/`retentionOfferAccepted` are optional metadata,
// never a precondition (spec).

import { ApiError, cancelSubscription } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { CancelSubscriptionResponse } from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

type Step = 'confirm' | 'retention' | 'survey';

interface CancelSubscriptionDialogProps {
  open: boolean;
  currentPeriodEnd: string | null;
  onOpenChange: (open: boolean) => void;
  onCanceled: (result: CancelSubscriptionResponse) => void;
}

export function CancelSubscriptionDialog({
  open,
  currentPeriodEnd,
  onOpenChange,
  onCanceled,
}: CancelSubscriptionDialogProps) {
  const { t, i18n } = useTranslation('subscription');
  const [step, setStep] = useState<Step>('confirm');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setStep('confirm');
      setReason('');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  async function submitCancellation(options: { includeReason: boolean }) {
    setSubmitting(true);
    setError(null);
    try {
      const result = await cancelSubscription({
        reason: options.includeReason ? reason.trim() || undefined : undefined,
        retentionOfferAccepted: false,
      });
      onCanceled(result);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(translateApiError(err, t, { fallback: t('cancelDialog.cancelError') }));
      } else {
        setError(translateApiError(err, t, { fallback: t('cancelDialog.cancelError') }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {step === 'confirm' && (
          <>
            <DialogHeader>
              <DialogTitle>{t('cancelDialog.confirm.title')}</DialogTitle>
              <DialogDescription>
                {currentPeriodEnd
                  ? t('cancelDialog.confirm.descriptionWithDate', {
                      date: formatDate(currentPeriodEnd, i18n.language),
                    })
                  : t('cancelDialog.confirm.descriptionNoDate')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                {t('cancelDialog.confirm.neverMind')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setStep('retention')}
              >
                {t('cancelDialog.confirm.continueCanceling')}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'retention' && (
          <>
            <DialogHeader>
              <DialogTitle>{t('cancelDialog.retention.title')}</DialogTitle>
              <DialogDescription>{t('cancelDialog.retention.description')}</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
                {t('cancelDialog.retention.keepSubscription')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setStep('survey')}
              >
                {t('cancelDialog.retention.continueCanceling')}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'survey' && (
          <>
            <DialogHeader>
              <DialogTitle>{t('cancelDialog.survey.title')}</DialogTitle>
              <DialogDescription>{t('cancelDialog.survey.description')}</DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-1.5">
              <Label htmlFor="cancel-reason">{t('cancelDialog.survey.reasonLabel')}</Label>
              <textarea
                id="cancel-reason"
                rows={3}
                maxLength={2000}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={submitting}
                placeholder={t('cancelDialog.survey.reasonPlaceholder')}
                className="w-full rounded-sm border border-jq-sand bg-jq-surface-0 px-3 py-2 text-[14px] text-jq-charcoal shadow-sm transition focus:border-jq-charcoal focus:outline-none focus:ring-1 focus:ring-jq-charcoal disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            {error && (
              <p className="mt-4 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
                {error}
              </p>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void submitCancellation({ includeReason: false })}
                disabled={submitting}
              >
                {t('cancelDialog.survey.skipAndCancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => void submitCancellation({ includeReason: true })}
                disabled={submitting}
              >
                {submitting
                  ? t('cancelDialog.survey.canceling')
                  : t('cancelDialog.survey.cancelSubscription')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
